import { motion } from "framer-motion";
import { MapPin, Phone, Instagram, Facebook, Copy } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../data";
import { Logo } from "./Logo";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const Contact = () => {
  const copy = (num) => {
    navigator.clipboard?.writeText(num);
    toast.success("Phone number copied", { description: num });
  };

  return (
    <footer id="contact" className="relative overflow-hidden bg-[#FCFBF8] pt-24 sm:pt-32 lg:pt-40">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-12 lg:px-16">
        <div className="mb-6 flex items-center gap-4">
          <span className="h-px w-14 bg-[#C5A059]" />
          <span className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-[#A37E3A]">
            Let&apos;s Begin
          </span>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading text-[16vw] font-light leading-[0.85] tracking-tighter text-[#1A1918] sm:text-[13vw] lg:text-[11rem]"
        >
          Let&apos;s <span className="italic text-gradient-gold">celebrate</span>
        </motion.h2>

        <div className="mt-16 grid grid-cols-1 gap-12 border-t border-[#E6E2D6] pt-14 md:grid-cols-3">
          {/* Location */}
          <div>
            <div className="mb-4 flex items-center gap-2 text-[#A37E3A]">
              <MapPin size={16} />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.25em]">Visit Us</span>
            </div>
            <a
              href={CONTACT.maps}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-address"
              className="font-heading text-2xl font-light leading-snug text-[#1A1918] transition-colors duration-300 hover:text-[#A37E3A]"
            >
              {CONTACT.address}
            </a>
          </div>

          {/* Phones */}
          <div>
            <div className="mb-4 flex items-center gap-2 text-[#A37E3A]">
              <Phone size={16} />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.25em]">Call Us</span>
            </div>
            <div className="flex flex-col gap-3">
              {CONTACT.phones.map((p, i) => (
                <div key={p} className="flex items-center gap-3">
                  <a
                    href={`tel:${CONTACT.phonesRaw[i]}`}
                    data-testid={`contact-phone-${i}`}
                    className="font-heading text-2xl font-light text-[#1A1918] transition-colors duration-300 hover:text-[#A37E3A]"
                  >
                    {p}
                  </a>
                  <button
                    onClick={() => copy(p)}
                    data-testid={`copy-phone-${i}`}
                    aria-label="Copy phone number"
                    className="text-[#5C5A56] transition-colors duration-300 hover:text-[#C5A059]"
                  >
                    <Copy size={15} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Socials */}
          <div>
            <div className="mb-4 flex items-center gap-2 text-[#A37E3A]">
              <span className="font-body text-xs font-semibold uppercase tracking-[0.25em]">Follow & Chat</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-whatsapp"
                className="inline-flex items-center gap-2 rounded-full bg-[#1A1918] px-5 py-3 font-body text-sm text-[#FCFBF8] transition-colors duration-300 hover:bg-[#25D366] hover:text-white"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp
              </a>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A1918]/20 px-5 py-3 font-body text-sm text-[#1A1918] transition-colors duration-300 hover:border-[#C51F5D] hover:text-[#C51F5D]"
              >
                <Instagram size={16} /> Instagram
              </a>
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="inline-flex items-center gap-2 rounded-full border border-[#1A1918]/20 px-5 py-3 font-body text-sm text-[#1A1918] transition-colors duration-300 hover:border-[#1877F2] hover:text-[#1877F2]"
              >
                <Facebook size={16} /> Facebook
              </a>
            </div>
            <p className="mt-4 font-body text-sm text-[#5C5A56]">{CONTACT.instagramHandle}</p>
          </div>
        </div>

        <div className="mt-20 flex flex-col items-center justify-between gap-6 border-t border-[#E6E2D6] py-8 md:flex-row">
          <Logo />
          <p className="font-body text-xs tracking-wide text-[#5C5A56]">
            © {new Date().getFullYear()} Golden Decor · Weddings & Events · Mangaluru
          </p>
        </div>
      </div>
    </footer>
  );
};
